import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { Avatar, Box, IconButton, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { Controller, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Delete } from 'react-iconly';
import useImageUploadStyles from './useImageUploadStyles';
const DEFAULT_IMAGE_TYPES = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'];
const buildUploaderTypes = (types) => {
    // react-drag-drop-files uses file extensions; be defensive about case-sensitivity
    const cleaned = types.map((t) => t.replace('.', '').trim()).filter(Boolean);
    const upper = cleaned.map((t) => t.toUpperCase());
    const lower = cleaned.map((t) => t.toLowerCase());
    return Array.from(new Set([...upper, ...lower]));
};
// Type guard to check if value is a File
const isFile = (value) => {
    return (value instanceof File ||
        (typeof value === 'object' &&
            value !== null &&
            'name' in value &&
            'size' in value &&
            'type' in value));
};
const isAllowedImageFile = (file, allowedTypes) => {
    const allowedUpper = allowedTypes.map((t) => t.replace('.', '').trim().toUpperCase());
    // If the browser provides a MIME type, it must be an image
    if (file.type && !file.type.toLowerCase().startsWith('image/'))
        return false;
    // Validate by extension first (this also prevents images like SVG/HEIC if not allowed)
    const ext = file.name?.split('.').pop()?.toUpperCase() ?? '';
    if (!!ext && allowedUpper.includes(ext))
        return true;
    // If extension is missing, fall back to MIME (rare but possible)
    return !!file.type && file.type.toLowerCase().startsWith('image/');
};
const normalizeToSingleFile = (fileOrFiles) => {
    if (!fileOrFiles)
        return null;
    // File
    if (fileOrFiles instanceof File)
        return fileOrFiles;
    // FileList
    if (typeof FileList !== 'undefined' && fileOrFiles instanceof FileList) {
        return fileOrFiles.length ? fileOrFiles.item(0) : null;
    }
    // Array<File>
    if (Array.isArray(fileOrFiles)) {
        const first = fileOrFiles[0];
        return first instanceof File ? first : null;
    }
    return null;
};
const ImageUpload = ({ control, name, edit, rules, types, centerVariant = true, label }) => {
    const { t } = useTranslation();
    const [imageSrc, setImageSrc] = useState(null);
    const [fileError, setFileError] = useState('');
    const styles = useImageUploadStyles(centerVariant);
    const theme = useTheme();
    const { field } = useController({ control, name });
    const value = field.value;
    // Combined button container styles
    const buttonContainerStyles = {
        position: 'absolute',
        bottom: 12,
        right: 16,
        display: 'flex',
        gap: 1,
        zIndex: 7,
        '& label': {
            outline: 'none !important'
        },
        '& label:focus-within': {
            outline: 'none !important'
        },
        '& input:focus': {
            outline: 'none !important'
        }
    };
    // If value is a string, use it as the image src
    const previewUrl = typeof value === 'string' && value
        ? value
        : isFile(value)
            ? URL.createObjectURL(value)
            : undefined;
    return (_jsx(Controller, { name: name, rules: rules, control: control, render: ({ field: { value, onChange }, fieldState: { error } }) => {
            useEffect(() => {
                if (typeof value === 'string') {
                    setImageSrc(value);
                }
                else if (isFile(value)) {
                    const localUrl = URL.createObjectURL(value);
                    setImageSrc(localUrl);
                }
                else {
                    setImageSrc(null);
                }
            }, [value]);
            const effectiveTypes = types && types.length > 0 ? types : DEFAULT_IMAGE_TYPES;
            const uploaderTypes = buildUploaderTypes(effectiveTypes);
            const fileTypeError = () => setFileError(t('users.fileTypeError'));
            const fileSizeError = () => setFileError(t('users.fileSizeError'));
            const onImageUpload = (fileOrFiles) => {
                const file = normalizeToSingleFile(fileOrFiles);
                if (!file)
                    return;
                // Extra guard: even with accept/types, drag-drop can still try invalid files
                if (!isAllowedImageFile(file, effectiveTypes)) {
                    fileTypeError();
                    onChange(null);
                    return;
                }
                const localUrl = URL.createObjectURL(file);
                setImageSrc(localUrl);
                onChange(file);
                setFileError('');
            };
            const onImageDelete = () => {
                if (imageSrc && imageSrc.startsWith('blob:')) {
                    URL.revokeObjectURL(imageSrc);
                }
                setImageSrc(null);
                onChange(null);
            };
            return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                            display: 'flex',
                            justifyContent: centerVariant ? 'center' : 'flex-start',
                            position: 'relative'
                        }, zIndex: 0, width: '100%', children: !imageSrc ? (_jsx(FileUploader, { fileOrFiles: value, handleChange: onImageUpload, 
                            // types={['JPG', 'JPEG', 'PNG']}
                            // maxSize={2}
                            types: uploaderTypes, onTypeError: fileTypeError, onSizeError: fileSizeError, children: _jsxs(Box, { sx: { ...styles.outerCircularContainer }, children: [_jsxs(Box, { sx: { ...styles.innerCircularContainer }, children: [_jsx(Icon, { icon: 'solar:camera-add-bold', fontSize: 24, color: centerVariant ? 'white' : theme.palette.text.disabled }), _jsx(Typography, { variant: 'caption', color: centerVariant ? 'white' : theme.palette.text.disabled, textAlign: 'center', children: label ?? t('users.uploadImage') })] }), centerVariant && (_jsx(Avatar, { src: previewUrl ?? '/images/avatar1.png', sx: {
                                            width: 76,
                                            height: 76,
                                            '& .MuiAvatar-img': {
                                                objectFit: 'contain'
                                            }
                                        } }))] }) })) : (_jsxs(Box, { sx: { ...styles.deleteImage, position: 'relative' }, children: [_jsx(Avatar, { src: imageSrc, sx: {
                                        width: 128,
                                        height: 128,
                                        '& .MuiAvatar-img': {
                                            objectFit: 'contain'
                                        }
                                    } }), _jsxs(Box, { sx: buttonContainerStyles, children: [edit && (_jsx(FileUploader, { fileOrFiles: value, handleChange: onImageUpload, types: uploaderTypes, maxSize: 30, onTypeError: fileTypeError, onSizeError: fileSizeError, children: _jsx(IconButton, { disableRipple: true, sx: {
                                                    ...styles.editIconButton,
                                                    position: 'static',
                                                    backgroundColor: theme.palette.grey[200],
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.grey[300]
                                                    },
                                                    '&:focus': {
                                                        border: 'none',
                                                        outline: 'none',
                                                        boxShadow: 'none'
                                                    }
                                                }, children: _jsx(Icon, { icon: 'eva:edit-outline', color: theme.palette.grey[500] }) }) })), !edit && (_jsx(IconButton, { onClick: onImageDelete, color: 'error', sx: {
                                                ...styles.deleteIconButton,
                                                position: 'static'
                                            }, children: _jsx(Delete, { size: 16 }) }))] })] })) }), error && (_jsx(Typography, { variant: 'caption', color: 'error', children: error.message })), !error && fileError && (_jsx(Typography, { variant: 'caption', color: 'error', children: fileError }))] }));
        } }));
};
export default ImageUpload;
